import type CollapsiblePanel from './CollapsiblePanel.js'
import type CollapsibleTrigger from './CollapsibleTrigger.js'

import { isServer, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { observe } from '../common/decorators/observe.js'
import { NordEvent } from '../common/events.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import style from './Collapsible.css'

let collapsibleCount = 1

/**
 * Event dispatched when a collapsible is opened or closed, either by the user
 * activating its trigger or programmatically toggling the `open` property.
 */
export class CollapsibleToggleEvent extends NordEvent {
  declare open: boolean

  constructor(open: boolean) {
    super('toggle')
    Object.defineProperty(this, 'open', {
      value: open,
      enumerable: true,
      writable: false,
    })
  }
}

/**
 * Collapsible is a disclosure that shows or hides a section of content. It wires
 * together a [Collapsible Trigger](/components/collapsible-trigger/) and a
 * [Collapsible Panel](/components/collapsible-panel/), animating the panel
 * height as it opens and closes.
 *
 * @status draft
 * @category container
 * @lightDom true
 *
 * @slot - Default slot for the [Collapsible Trigger](/components/collapsible-trigger/), [Collapsible Panel](/components/collapsible-panel/) and any other content.
 *
 * @fires {CollapsibleToggleEvent} toggle - Dispatched when the collapsible is opened or closed. The event includes an `open` property reflecting the new state.
 */
@customElement('nord-collapsible')
export default class Collapsible extends DraftComponentMixin(LitElement) {
  private collapsibleId = `nord-collapsible-${collapsibleCount++}`

  /**
   * Whether the collapsible is open.
   */
  @property({ reflect: true, type: Boolean }) open = false

  /**
   * Whether the collapsible should ignore user interaction.
   */
  @property({ reflect: true, type: Boolean }) disabled = false

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)
    this.setup()
  }

  // Render in the light DOM so the trigger and panel are the element's own
  // content and the injected stylesheet applies (no shadow boundary, like Table).
  protected createRenderRoot() {
    return this
  }

  private get trigger() {
    return this.querySelector<CollapsibleTrigger>('nord-collapsible-trigger')
  }

  private get panel() {
    return this.querySelector<CollapsiblePanel>('nord-collapsible-panel')
  }

  /**
   * Wire up the ARIA relationship between the trigger and the panel and reflect
   * the initial open state to both.
   */
  private setup() {
    if (isServer) {
      return
    }

    const { trigger, panel } = this

    if (panel) {
      panel.id ||= `${this.collapsibleId}-panel`
      panel.open = this.open
    }

    if (trigger) {
      trigger.id ||= `${this.collapsibleId}-trigger`
      trigger.open = this.open
      trigger.disabled = this.disabled
      if (panel) {
        trigger.setAttribute('aria-controls', panel.id)
        panel.setAttribute('aria-labelledby', trigger.id)
      }
    }
  }

  @observe('open')
  protected handleOpenChange(oldValue: boolean | undefined, newValue: boolean) {
    this.syncState()

    // Skip the initial assignment so we don't fire on first render.
    if (oldValue !== undefined && oldValue !== newValue) {
      this.dispatchEvent(new CollapsibleToggleEvent(newValue))
    }
  }

  @observe('disabled')
  protected handleDisabledChange() {
    const { trigger } = this
    if (trigger) {
      trigger.disabled = this.disabled
    }
  }

  /**
   * Push the current open state down to the trigger and panel.
   */
  private syncState() {
    const { trigger, panel } = this

    if (trigger) {
      trigger.open = this.open
    }

    if (panel) {
      panel.open = this.open
    }
  }

  /**
   * Toggle the collapsible open or closed. Called by the trigger.
   */
  toggle() {
    if (this.disabled) {
      return
    }

    this.open = !this.open
  }

  /**
   * Open the collapsible programmatically.
   */
  expand() {
    if (!this.disabled) {
      this.open = true
    }
  }

  /**
   * Close the collapsible programmatically.
   */
  collapse() {
    if (!this.disabled) {
      this.open = false
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-collapsible': Collapsible
  }
}
